import { useEffect, useState, useMemo, useCallback } from 'react';

import { GraphQLClient } from 'graphql-request';

import { QueryClientProvider } from '@tanstack/react-query';

import { AppContext } from './App.context';
import { useDb, useInstalled, useResize } from './App.hooks';
import config from './config.window';

import {
	useDeviceInformation,
	useDocumentTitle,
	usePrintStatus,
	useDialog,
	useIsOnline,
	usePersistentStorage,
} from '@shared/lib/hooks';

import { Password, usePassword } from '@pages/password/Password';
import {
	getGraphQLClient,
	AUTH_KEY,
	AUTH_EXPIRATION_DATE_KEY,
	AUTH_SUBJECT_KEY,
	REFRESH_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRATION_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
	FEATURES_KEY,
	IS_SUBSCRIBED_KEY,
} from '@shared/auth/Authentication.hooks';

import { About } from '@widgets/about/About';

import LocalStorageKV from '@shared/lib/kv/LocalStorageKV';
import {
	defaultQueryClient,
	bootQueryPersistence,
	setCacheDurationsForConnectivity,
} from '@shared/query-client/QueryClient';
import { setUnauthenticatedHandler } from '@shared/api';

// Lets services/api/client.ts invalidate the cache on a 401 without importing
// the QueryClient itself (which would rebuild the services -> query-client ->
// views/templates import cycle). Module scope, not an effect: a 401 can land
// before any effect has run.
setUnauthenticatedHandler(() => defaultQueryClient.invalidateQueries());

import {
	ALERT_TIMEOUT_KEYS,
	USER_ROLE,
	ACCESS_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRE_AT_KEY,
	APP_USER_ID,
	BeforeInstallPromptEvent,
} from '@/constants';
import { Tracking } from '@/tracking/Tracking';
import Template from '@/templates/Template';
import { ServiceWorkerProvider } from '@shared/sw/sw.context';
import { useBuildVersionCheck } from '@shared/sw/sw.version';

import './App.css';

declare global {
	interface Window {
		onbeforeinstallprompt?: unknown;
		toggleDevtools?: () => void;
	}
}

function App() {
	const isOnline = useIsOnline();
	const { contentProtection } = config.app;
	const [_showDevtools, setShowDevtools] = useState(false);
	const device = useDeviceInformation();
	const { appKey, languages, language, title } = config.build;

	const [graphQLClient, setGraphQLClient] =
		useState<GraphQLClient>(getGraphQLClient());

	// defaultQueryClient is a module-level singleton (see QueryClient.ts) --
	// no state wrapper needed, unlike the old useState<QueryClient>(...) plus
	// an effect that rewrote networkMode on every useIsOnline() change and
	// called setQueryClient with the *same* reference (a no-op re-render,
	// since QueryClient mutates in place). networkMode is a static default
	// now; gcTime and the persister's maxAge are the two settings still
	// intentionally connectivity-dependent (2h online / 24h offline, a
	// resource-management decision, not a correctness one -- see
	// setCacheDurationsForConnectivity's own comment), so they're still
	// updated reactively below, but narrowly: only those two fields change,
	// and nothing forces a re-render to do it.
	const queryClient = defaultQueryClient;

	useBoot();
	useBuildVersionCheck();

	// Ordered before bootQueryPersistence's effect (declaration order is
	// execution order for effects in the same component) so the very first
	// restore/gc pass already uses the right maxAge instead of the module's
	// safe-default one.
	useEffect(() => {
		setCacheDurationsForConnectivity(!!isOnline);
		if (!isOnline) {
			alert('You are offline');
		}
	}, [isOnline]);

	useEffect(() => {
		bootQueryPersistence(queryClient);
	}, [queryClient]);

	const kv = useMemo(() => new LocalStorageKV('cf-kv'), []);

	const isSupportInstallPrompt =
		typeof window.onbeforeinstallprompt === 'object';

	const [contentCSS, setContentCSS] = useState('');
	const [deferredPrompt, setDeferredPrompt] = useState<
		BeforeInstallPromptEvent | undefined
	>(undefined);

	const isInstalled = useInstalled();
	usePersistentStorage(isInstalled);
	const { shouldDisplayPassword, onSuccessPassword } = usePassword();
	const { show: showDialog, setShowDialog } = useDialog();

	const isTouchable = useMemo(
		() => window.matchMedia('(pointer: coarse)').matches,
		[],
	);

	// Lazy on purpose: every consumer (logout, auth callback reset) needs to
	// trigger this at the moment it actually happens, not have it already
	// fired at boot. useMemo(() => clearCache(), []) previously invoked
	// clearCache() immediately on mount instead of handing back a callable
	// -- clearCache() deletes every entry in Cache Storage with no
	// filtering, so that wiped the precached app shell and every downloaded
	// issue's images on every single app open, online or offline, before
	// the user did anything. Consumers were awaiting the *same already-
	// settled* promise later at logout, which looked like it worked (the
	// .then() still fires) but the deletion had nothing to do with logout.
	const clearCacheFunction = useCallback(() => clearCache(), []);

	useEffect(() => {
		window.toggleDevtools = () => setShowDevtools((old) => !old);
	}, []);
	useEffect(() => {
		localStorage.setItem('app-mode', `${import.meta.env.MODE}`);

		if (isSupportInstallPrompt) {
			window.addEventListener('beforeinstallprompt', (e) => {
				// Prevents the default mini-infobar or install dialog from appearing on mobile
				e.preventDefault();
				setDeferredPrompt(e as unknown as BeforeInstallPromptEvent);
				return false;
			});
		}
		if (contentProtection) {
			document.body.oncontextmenu = (e) => {
				e.preventDefault();
				return false;
			};
			setContentCSS(`@media print {
				html, body {
					   display: none;  /* hide whole page */
				}
			}`);
		}

		// Clear timeouts
		if (sessionStorage.getItem(ALERT_TIMEOUT_KEYS.ID)) {
			sessionStorage.removeItem(ALERT_TIMEOUT_KEYS.ID);
		}

		if (sessionStorage.getItem(ALERT_TIMEOUT_KEYS.DATETIME)) {
			sessionStorage.removeItem(ALERT_TIMEOUT_KEYS.DATETIME);
		}
	}, [isSupportInstallPrompt, contentProtection]);
	useResize();
	useDocumentTitle(title);

	const isPrinting = usePrintStatus();
	const { db } = useDb();

	const [lang, setLang] = useState(
		localStorage.getItem('language')
			? localStorage.getItem('language')
			: language,
	);

	const onLanguageChange = useCallback((language: string) => {
		localStorage.setItem('language', language);
		setLang(language);
	}, []);

	const rebuildGraphQLClient = useCallback(() => {
		setGraphQLClient(getGraphQLClient());
	}, []);

	const parseGraphQLClientError = useCallback((err: unknown) => {
		try {
			const reqRes = JSON.parse(JSON.stringify(err));
			return new Error(reqRes.response.errors[0].message);
		} catch {
			throw err;
		}
	}, []);

	const contextValue = useMemo(
		() => ({
			db: db ?? undefined,
			appKey,
			graphQLClient,
			baseUrl: config?.build?.subdirectory ?? '/',
			lang: lang ?? 'en',
			languages,
			onLanguageChange,
			onlineStatus: !!isOnline,
			isPrinting,
			isInstalled,
			device,
			isTouchable,
			isSupportInstallPrompt,
			deferredPrompt,
			kv,
			clearCache: clearCacheFunction,
			rebuildGraphQLClient,
			parseGraphQLClientError,
		}),
		[
			db,
			appKey,
			device,
			graphQLClient,
			lang,
			languages,
			onLanguageChange,
			isOnline,
			isPrinting,
			isInstalled,
			isTouchable,
			isSupportInstallPrompt,
			deferredPrompt,
			kv,
			clearCacheFunction,
			rebuildGraphQLClient,
			parseGraphQLClientError,
		],
	);

	if (isOnline === null || !db || !kv) {
		return null;
	}

	if (shouldDisplayPassword) {
		return <Password onSuccess={onSuccessPassword} />;
	}

	return (
		<ServiceWorkerProvider>
			<AppContext.Provider value={contextValue}>
				<QueryClientProvider client={queryClient}>
					<Tracking>
						<Template />
					</Tracking>

					{contentCSS ? <style>{contentCSS}</style> : null}
					<About
						show={showDialog}
						setShow={(v: boolean) => {
							setShowDialog(v);
						}}
					/>
				</QueryClientProvider>
			</AppContext.Provider>
		</ServiceWorkerProvider>
	);
}

async function clearCache(): Promise<void> {
	const promises = [];
	const keyList = await caches.keys();
	for (const key of keyList) {
		promises.push(caches.delete(key));
	}

	await Promise.all(promises);
}

function useBoot() {
	const setBuildValues = () => {
		localStorage.setItem('app_key', config.build.appKey);
		localStorage.setItem('version', config.build.version);
	};
	useEffect(() => {
		const version = localStorage.getItem('version');
		const appKey = localStorage.getItem('app_key');
		if (!appKey || !version) {
			clearLocalStorage();
			setBuildValues();
			return;
		}

		if (
			appKey !== config.build.appKey ||
			version !== config.build.version
		) {
			clearLocalStorage();
			setBuildValues();
			return;
		}
	}, []);
}

// Keys a deploy (version/app_key bump) must never wipe: doing so signs the
// user out on every release. Everything else in localStorage is treated as
// disposable cache and cleared, per the version/app_key mismatch this guards.
const AUTH_STORAGE_KEYS = new Set([
	'favorites',
	AUTH_KEY,
	AUTH_EXPIRATION_DATE_KEY,
	AUTH_SUBJECT_KEY,
	REFRESH_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRATION_KEY,
	REFRESH_TOKEN_EXPIRATION_KEY,
	FEATURES_KEY,
	IS_SUBSCRIBED_KEY,
	USER_ROLE,
	ACCESS_TOKEN_KEY,
	ACCESS_TOKEN_EXPIRE_AT_KEY,
	APP_USER_ID,
]);

function clearLocalStorage() {
	for (const key in localStorage) {
		if (AUTH_STORAGE_KEYS.has(key)) {
			continue;
		}

		localStorage.removeItem(key);
	}
}

export default App;
